import axios from "axios";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader } from "../../utils/util";
import { dateKey } from "../../utils/listData";

export const LOW_STOCK = 10;
export const number = (value) =>
  value !== null && value !== "" && Number.isFinite(Number(value)) ? Number(value) : 0;
export const money = (value) =>
  value === null || value === undefined || value === "" || !Number.isFinite(Number(value))
    ? "—"
    : new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(Number(value));
export const toolsDate = (value) =>
  !value || Number.isNaN(new Date(value).getTime())
    ? "—"
    : new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium" }).format(
        new Date(value),
      );
export const stockState = (stock) =>
  stock === null ||
  stock === undefined ||
  stock === "" ||
  !Number.isSafeInteger(Number(stock)) ||
  Number(stock) < 0
    ? "unknown"
    : Number(stock) === 0
      ? "out"
      : Number(stock) <= LOW_STOCK
        ? "low"
        : "healthy";
export const statusColor = (status) =>
  ({
    pending: "warning",
    processing: "info",
    accepted: "info",
    shipped: "info",
    delivered: "success",
    paid: "success",
    rejected: "error",
    cancelled: "error",
  })[status] || "default";
export const statusLabel = (status) =>
  ({
    pending: "Pending",
    processing: "Under review",
    accepted: "Accepted",
    shipped: "Shipped",
    delivered: "Delivered",
    paid: "Paid",
    rejected: "Rejected",
    cancelled: "Cancelled",
  })[status] ||
  status ||
  "Unspecified";
export const orderTotal = (order) => {
  const summary = order?.orderSummary;
  if (!summary) return null;
  const valid = (value) =>
    value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));
  // Shipping is added during approval. Prefer the current charge breakdown
  // when it is present, since a stored cart total may predate that charge.
  if (
    valid(summary.subtotal) &&
    (summary.gst == null || valid(summary.gst)) &&
    (summary.shipping == null || valid(summary.shipping))
  ) {
    return Number(summary.subtotal) + number(summary.gst) + number(summary.shipping);
  }
  return valid(summary.total) ? Number(summary.total) : null;
};
export const needsReceiptReview = (row) =>
  Boolean(row.receiptUrl) && ["pending", "processing"].includes(row.paymentStatus);
export const categoryNames = (data) =>
  [
    ...new Set(
      (Array.isArray(data) ? data : data?.data || [])
        .map((row) => (typeof row === "string" ? row : row.category))
        .filter(Boolean),
    ),
  ].sort();

// Read every server page before deriving catalogue-wide or all-time figures.
// An incomplete or repeating response is an error, never a successful partial total.
export async function fetchToolsPages(endpoint) {
  const rows = [],
    seen = new Set();
  for (let page = 1; page <= 10000; page += 1) {
    const { data } = await axios.get(API_ENDPOINT + endpoint, {
      ...getHeader(),
      params: { page, limit: 100 },
    });
    if (!Array.isArray(data?.data))
      throw new Error("Unable to read the market data. Please retry.");
    const items = data.data;
    const count = (value) =>
      value === null || value === undefined || value === "" ? NaN : Number(value);
    const pages = count(data.pagination?.totalPages ?? data.totalPages);
    const total = count(
      data.pagination?.totalItems ?? data.pagination?.total ?? data.totalItems ?? data.total,
    );
    const pageSize = count(data.pagination?.limit ?? data.pagination?.pageSize ?? data.pageSize);
    let added = 0;
    items.forEach((row) => {
      if (!row || typeof row !== "object" || (!row._id && !row.orderId))
        throw new Error("A market record is missing its reference. Please retry.");
      const key = row._id || row.orderId;
      if (key && seen.has(String(key))) return;
      if (key) seen.add(String(key));
      rows.push(row);
      added += 1;
    });
    if (!items.length) {
      if ((Number.isFinite(total) && rows.length < total) || (pages > 1 && page <= pages))
        throw new Error("The market list is incomplete. Please refresh.");
      return rows;
    }
    if (!added) throw new Error("The market list changed while loading. Please refresh.");
    if (
      (Number.isFinite(pages) && pages > 0 && page >= pages) ||
      (Number.isFinite(total) && rows.length >= total)
    ) {
      if (Number.isFinite(total) && rows.length < total)
        throw new Error("The market list is incomplete. Please refresh.");
      return rows;
    }
    if (
      !Number.isFinite(total) &&
      !Number.isFinite(pages) &&
      pageSize > 0 &&
      items.length < pageSize
    )
      return rows;
  }
  throw new Error("Too many pages to load. Please narrow the list.");
}

export function summarizeMarket(products, orders, payments, now = new Date()) {
  const paidOrders = orders.filter(
    (row) => row.paymentStatus === "paid" && !["rejected", "cancelled"].includes(row.orderStatus),
  );
  const paidAmounts = paidOrders.map(orderTotal);
  const [year, month] = dateKey(now).split("-").map(Number);
  const months = Array.from({ length: 6 }, (_, index) => {
    const key = new Date(Date.UTC(year, month - 6 + index, 1)).toISOString().slice(0, 7);
    const matching = orders.filter((row) => dateKey(row.createdOn).slice(0, 7) === key);
    return {
      key,
      label: new Intl.DateTimeFormat("en-IN", { month: "short", timeZone: "UTC" }).format(
        new Date(key + "-01"),
      ),
      orders: matching.length,
    };
  });
  return {
    products: products.length,
    units: products.some((row) => stockState(row.stock) === "unknown")
      ? null
      : products.reduce((total, row) => total + number(row.stock), 0),
    low: products.filter((row) => stockState(row.stock) === "low").length,
    out: products.filter((row) => stockState(row.stock) === "out").length,
    orders: orders.length,
    pending: orders.filter((row) => row.orderStatus === "pending").length,
    receipts: payments.filter(needsReceiptReview).length,
    collected: paidAmounts.some((value) => value === null)
      ? null
      : paidAmounts.reduce((sum, value) => sum + value, 0),
    paidOrders: paidOrders.length,
    months,
  };
}

export const invalidateTools = (client) =>
  client.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "tools-market" ||
      query.queryKey.some(
        (key) =>
          typeof key === "string" &&
          /get-tools|tools-transactions|admin\/tool\/|product-categories|product-subcategories|product-settings|admin\/banners/.test(
            key,
          ),
      ),
  });

export function nextStock(current, mode, amount) {
  if (!["add", "remove", "set"].includes(mode)) throw new Error("Choose a valid stock operation.");
  if (stockState(current) === "unknown")
    throw new Error("Current stock is unavailable. Edit the product to correct it.");
  if (
    amount === "" ||
    !Number.isSafeInteger(Number(amount)) ||
    Number(amount) < 0 ||
    (mode !== "set" && Number(amount) === 0)
  )
    throw new Error(
      "Enter a whole number" + (mode === "set" ? " of zero or more." : " greater than zero."),
    );
  const next =
    mode === "set"
      ? Number(amount)
      : number(current) + (mode === "remove" ? -1 : 1) * Number(amount);
  if (!Number.isSafeInteger(next) || next < 0)
    throw new Error("Stock cannot be negative or exceed the supported quantity.");
  return next;
}

export async function saveStock(product, mode, amount) {
  const stock = nextStock(product.stock, mode, amount);
  const { data: current } = await axios.get(
    `${API_ENDPOINT}admin/tool/${encodeURIComponent(product._id)}`,
    getHeader(),
  );
  if (stockState(current?.stock) === "unknown")
    throw new Error("Current stock is unavailable. Refresh the product and try again.");
  if (number(current.stock) !== number(product.stock))
    throw new Error(
      "Stock changed since you opened this product. Close this dialog and refresh before trying again.",
    );
  const form = new FormData();
  [
    "name",
    "description",
    "mrp",
    "sellingPrice",
    "discount",
    "category",
    "gst",
    "weight",
    "weightUnit",
  ].forEach((key) => form.append(key, current[key] ?? ""));
  form.append("stock", String(stock));
  const images = Array.isArray(current.images)
    ? current.images
    : current.imageUrl
      ? [current.imageUrl]
      : [];
  form.append("keptImageUrls", JSON.stringify(images));
  const headers = { ...getHeader().headers };
  Object.keys(headers).forEach((key) => {
    if (key.toLowerCase() === "content-type") delete headers[key];
  });
  await axios.post(`${API_ENDPOINT}admin/edit-tool/${encodeURIComponent(product._id)}`, form, {
    headers,
  });
  return stock;
}
